import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Palette as PaletteIcon, Store as StoreIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useOwnedThemes } from '../../hooks';
import { ThemeCard } from '../../components/theme';
import { useDocumentTitle } from '../../hooks/core';

export const MyThemesPage: React.FC = () => {
  useDocumentTitle('My Themes');
  const navigate = useNavigate();

  const { themes, isLoading, error, refresh } = useOwnedThemes();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PaletteIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" component="h1">
                My Themes
              </Typography>
              <Tooltip title="Learn more about themes">
                <IconButton
                  component={RouterLink}
                  to="/support/themes"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Themes you own and can use on your boards
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<StoreIcon />}
          onClick={() => navigate('/themes')}
        >
          Browse Store
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={refresh} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : themes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PaletteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No themes yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            You don't own any themes yet. Visit the Theme Store to browse and purchase themes.
          </Typography>
          <Button
            variant="contained"
            startIcon={<StoreIcon />}
            onClick={() => navigate('/themes')}
          >
            Visit Theme Store
          </Button>
        </Paper>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You own {themes.length} theme{themes.length !== 1 ? 's' : ''}. Use them on any of your boards!
          </Typography>
          <Grid container spacing={3}>
            {themes.map(theme => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={theme.id}>
                <ThemeCard
                  theme={theme}
                  showActionButton={false}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default MyThemesPage;
